import React, {useEffect, useState} from 'react';

import {Permissions} from 'auth/PermissionConstants';
import DocumentActions, {LATEST_DOCUMENT_VERSION} from 'notebook/documents/DocumentActions';
import {FromServerDocumentVersion} from 'notebook/documents/DocumentConstants';
import DocumentStore from 'notebook/documents/DocumentStore';
import {getDocumentSaveState} from 'notebook/documents/useDocumentSaveState';
import {ItemVersionState} from 'notebook/store/VersionedItemSubstore';
import VersionHistoryPanel from 'notebook/version-history/VersionHistoryPanel';
import {Version} from 'notebook/version-history/VersionHistoryTypes';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';

interface Props {
  id: string;
  title: string;
}

export function DocumentHistoryPanel({id, title}: Props): JSX.Element {
  const [versionState, setVersionState] = useState<ItemVersionState<FromServerDocumentVersion> | null>(null);
  const [permissions, setPermissions] = useState<Permissions | null>(null);
  // The latest saved version of the document, once it is open. It is kept with its document id so a value
  // read for a previous document is never used for this one.
  const [latestVersion, setLatestVersion] = useState<{id: string; versionId: number | undefined} | null>(
    null
  );

  const openVersion = (versionId: number): void => {
    DocumentActions.loadVersion(id, versionId);
  };

  useEffect(() => {
    const callback = (): void => {
      setVersionState(DocumentStore.getVersionState(id) ?? null);
      setPermissions(DocumentStore.getDocumentPermissions(id));
      setLatestVersion({id, versionId: getDocumentSaveState(id).latestVersionId});
    };

    let isCancelled = false;
    const openDocument = async (): Promise<void> => {
      const document = DocumentStore.getDocument(id);
      if (!document || document.isStale) {
        await DocumentActions.open(id, {documentVersionId: LATEST_DOCUMENT_VERSION});
      }
      // The effect can be cleaned up while the document is still opening. Nothing may be read or subscribed
      // afterwards, since the cleanup has already run and would never remove the listener.
      if (isCancelled) {
        return;
      }
      setLatestVersion({id, versionId: getDocumentSaveState(id).latestVersionId});
      DocumentStore.addChangeListener(callback);
    };
    openDocument();
    return () => {
      isCancelled = true;
      DocumentStore.removeChangeListener(callback);
    };
  }, [id]);

  // A save made while the panel is open changes the document's latest version, which fetches the list again.
  const latestVersionId = latestVersion?.id === id ? latestVersion.versionId : undefined;
  useEffect(() => {
    if (latestVersionId !== undefined) {
      DocumentActions.fetchVersions(id);
    }
  }, [id, latestVersionId]);

  // TODO(BNCH-99701): Add resetToVersion()

  // TODO(BNCH-99703): Add cloneFromVersion()

  if (versionState == null || permissions == null || versionState.versions.length === 0) {
    return <CenteredHorizontalThrobber />;
  }
  const openVersionId = versionState.openVersionId === false ? undefined : versionState.openVersionId;

  const versions: Array<Version> = versionState.versions.slice().reverse();
  return (
    <VersionHistoryPanel
      versions={versions}
      openVersionId={openVersionId}
      navigateAndSetOpenVersionId={openVersion}
      enableResetToVersion={false}
      resetToVersion={() => {}}
      enableDuplicateFromVersion={false}
      duplicateFromVersion={() => {}}
      unsaved={versionState.unsaved}
      headerTitle={title}
    />
  );
}
